import axios from "axios";
import { getCookie } from "../utils/csrf";
import {
  clearLegacyAuthTokens,
  legacyBearerAuthEnabled,
} from "./legacyAuth";

const INSTALL_MARKER = "__eternuaSecureFetchInstalled";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

function requestUrl(input) {
  if (typeof input === "string" || input instanceof URL) return String(input);
  return String(input?.url || "");
}

function isSameOrigin(input, currentWindow) {
  try {
    return new URL(requestUrl(input), currentWindow.location.href).origin === currentWindow.location.origin;
  } catch (_error) {
    return false;
  }
}

/**
 * Central browser boundary for cookie-authenticated API requests.
 * It keeps credentials same-origin and attaches Django's double-submit CSRF
 * token to every unsafe same-origin fetch, including older feature modules.
 */
export function installSecureRequestDefaults(currentWindow = window) {
  if (!currentWindow?.fetch || currentWindow[INSTALL_MARKER]) return;

  const allowLegacyBearer = legacyBearerAuthEnabled(currentWindow);
  if (!allowLegacyBearer) clearLegacyAuthTokens(currentWindow);
  const nativeFetch = currentWindow.fetch.bind(currentWindow);
  currentWindow.fetch = (input, init = {}) => {
    const requestMethod = input?.method || "GET";
    const method = String(init.method || requestMethod).toUpperCase();
    const sameOrigin = isSameOrigin(input, currentWindow);
    const headers = new Headers(init.headers || input?.headers || {});
    let removedLegacyHeader = false;
    if (
      sameOrigin
      && !allowLegacyBearer
      && /^Token\s/i.test(String(headers.get("Authorization") || ""))
    ) {
      headers.delete("Authorization");
      removedLegacyHeader = true;
    }
    if (!SAFE_METHODS.has(method) && sameOrigin) {
      if (!headers.has("X-CSRFToken")) {
        const csrfToken = getCookie("csrftoken");
        if (csrfToken) headers.set("X-CSRFToken", csrfToken);
      }
      return nativeFetch(input, {
        ...init,
        credentials: init.credentials || "same-origin",
        headers,
      });
    }
    if (removedLegacyHeader) {
      return nativeFetch(input, { ...init, headers });
    }
    return nativeFetch(input, init);
  };
  currentWindow[INSTALL_MARKER] = true;

  // Axios is still used by a few historical modules. Align its CSRF names
  // with Django so those calls follow the same cookie-authentication policy.
  axios.defaults.withCredentials = true;
  axios.defaults.xsrfCookieName = "csrftoken";
  axios.defaults.xsrfHeaderName = "X-CSRFToken";
}
